import React, { useState } from "react";
import {
  SearchIcon,
  EditIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  InfoIcon,
} from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  Select,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  HStack,
  VStack,
  Icon,
  Badge,
  Input,
  InputGroup,
  InputLeftElement,
  IconButton,
  Tooltip,
} from "@chakra-ui/react";

import { motion } from "framer-motion";
import { useRecoilState } from "recoil";
import { viewTenantsDrawerState } from "atoms/viewTenantsDrawer";
import ViewTenants from "./ViewTenants";
import CreateTenants from "./CreateTenants";
import { createTenantDrawerState } from "atoms/createTenantDrawerState";
import { updateTenantDrawerState } from "atoms/updateTenantDrawerState";
import { tenantTableHeaders } from "../variables/tenantTableHeaders";

// Motion components
const MotionBox = motion(Box);
const MotionFlex = motion(Flex);
const MotionTr = motion(Tr);

// Styled components
const GlassMorphismCard = ({ children, ...rest }) => {
  const bgColor = useColorModeValue(
    "rgba(255, 255, 255, 0.8)",
    "rgba(26, 32, 44, 0.8)",
  );
  const borderColor = useColorModeValue(
    "rgba(255, 255, 255, 0.5)",
    "rgba(255, 255, 255, 0.1)",
  );

  return (
    <Box
      bg={bgColor}
      backdropFilter="blur(10px)"
      borderRadius="2xl"
      boxShadow="xl"
      border="1px solid"
      borderColor={borderColor}
      overflow="hidden"
      transition="all 0.3s ease"
      _hover={{ boxShadow: "2xl" }}
      {...rest}
    >
      {children}
    </Box>
  );
};

const EmptyStateIllustration = () => (
  <MotionFlex
    direction="column"
    align="center"
    justify="center"
    py={12}
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
  >
    <Icon as={CheckCircleIcon} boxSize={16} color="#f57d36" mb={4} />
    <Text fontSize="xl" fontWeight="bold" mb={2}>
      No Tenants Found
    </Text>
    <Text color="gray.500" textAlign="center" maxW="md">
      There are no tenants available with the selected filter. Try changing your
      filter criteria.
    </Text>
  </MotionFlex>
);

const StatusBadge = ({ status }) => {
  let color, label, bgColor;

  switch (status) {
    case "verified":
      label = "Verified";
      bgColor = "green.400";
      color = "white";
      break;
    case "rejected":
      label = "Rejected";
      bgColor = "red.400";
      color = "white";
      break;
    case "pending":
      label = "Pending";
      bgColor = "orange.400";
      color = "white";
      break;
    case "not_initiated":
      label = "Not Initiated";
      bgColor = "blue.400";
      color = "white";
      break;
    default:
      label = "N/A";
      bgColor = "gray.400";
      color = "white";
      break;
  }

  return (
    <Badge
      px={3}
      py={1}
      borderRadius="full"
      bg={bgColor}
      color={color}
      fontSize="xs"
      fontWeight="medium"
    >
      {label}
    </Badge>
  );
};

const TypeBadge = ({ type }) => {
  let color, label;

  switch (type) {
    case "cpo":
      color = "purple";
      label = "CPO";
      break;
    case "bco":
      color = "blue";
      label = "BCO";
      break;
    case "cpo_operated_by_urzza":
      color = "teal";
      label = "CPO Operated By Urzza";
      break;
    default:
      color = "gray";
      label = "N/A";
  }

  return (
    <Badge
      px={3}
      py={1}
      borderRadius="full"
      colorScheme={color}
      variant="subtle"
      fontSize="xs"
      fontWeight="medium"
    >
      {label}
    </Badge>
  );
};

const GSTBadge = ({ isGstPaid }) => {
  let color, label, bgColor;

  if (isGstPaid === true) {
    label = "Yes";
    bgColor = "green.400";
    color = "white";
  } else if (isGstPaid === false) {
    label = "No";
    bgColor = "red.400";
    color = "white";
  } else {
    label = "N/A";
    bgColor = "gray.400";
    color = "white";
  }

  return (
    <Badge
      px={3}
      py={1}
      borderRadius="full"
      bg={bgColor}
      color={color}
      fontSize="xs"
      fontWeight="medium"
    >
      {label}
    </Badge>
  );
};

const PayoutsEnabledBadge = ({ isPayoutsEnabled }) => {
  let color, label, bgColor;

  if (isPayoutsEnabled === true) {
    label = "Yes";
    bgColor = "green.400";
    color = "white";
  } else if (isPayoutsEnabled === false) {
    label = "No";
    bgColor = "red.400";
    color = "white";
  } else {
    label = "N/A";
    bgColor = "gray.400";
    color = "white";
  }

  return (
    <Badge
      px={3}
      py={1}
      borderRadius="full"
      bg={bgColor}
      color={color}
      fontSize="xs"
      fontWeight="medium"
    >
      {label}
    </Badge>
  );
};

const PaginationButton = ({ children, isActive, isDisabled, onClick }) => {
  return (
    <Button
      size="sm"
      variant={isActive ? "solid" : "outline"}
      colorScheme={isActive ? "orange" : "gray"}
      isDisabled={isDisabled}
      onClick={onClick}
      minW="40px"
      h="40px"
      borderRadius="md"
      _hover={!isDisabled && { transform: "translateY(-2px)", shadow: "md" }}
      transition="all 0.2s"
    >
      {children}
    </Button>
  );
};

const TenantsList = (props) => {
  const [viewTenantsState, setViewTenantsState] = useRecoilState(
    viewTenantsDrawerState,
  );
  const [createDrawerState, setCreateDrawerState] = useRecoilState(
    createTenantDrawerState,
  );
  const [updateDrawerState, setUpdateDrawerState] = useRecoilState(
    updateTenantDrawerState,
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredTenants, setFilteredTenants] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(8);

  const {
    tenants = [],
    verificationStatus,
    setVerificationStatus,
    getTenants,
    setTenants,
  } = props;

  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  const textColor = useColorModeValue("#2D3748", "white");
  const hoverBg = useColorModeValue("#F7FAFC", "#2D3748");
  const theadBgColor = useColorModeValue("#F7FAFC", "#1A202C");
  const themeColor = "#f57d36";

  React.useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredTenants(tenants);
    } else {
      const lowercasedQuery = searchQuery.toLowerCase();
      const filtered = tenants.filter(
        (tenant) =>
          tenant.FirstName?.toLowerCase().includes(lowercasedQuery) ||
          tenant.LastName?.toLowerCase().includes(lowercasedQuery) ||
          tenant.NameSpace?.toLowerCase().includes(lowercasedQuery) ||
          tenant.PhoneNumber?.toLowerCase().includes(lowercasedQuery) ||
          tenant.PrimaryEmail?.toLowerCase().includes(lowercasedQuery),
      );
      setFilteredTenants(filtered);
    }
    // Reset to first page when search query changes
    setCurrentPage(0);
  }, [searchQuery, tenants]);

  const handleDownload = (url) => {
    window.open(url, "_blank");
  };

  const viewHandler = (tenant) => {
    setViewTenantsState({
      isOpen: true,
      data: tenant,
    });
  };

  const updateHandler = () => {
    setUpdateDrawerState(null);
    setCreateDrawerState(null);
    getTenants();
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Pagination logic
  const pageCount = Math.ceil(filteredTenants.length / pageSize);
  const paginatedData = React.useMemo(() => {
    const start = currentPage * pageSize;
    const end = start + pageSize;
    return filteredTenants.slice(start, end);
  }, [filteredTenants, currentPage, pageSize]);

  const gotoPage = (page) => {
    setCurrentPage(page);
  };

  const nextPage = () => {
    if (currentPage < pageCount - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const previousPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const canPreviousPage = currentPage > 0;
  const canNextPage = currentPage < pageCount - 1;

  return (
    <MotionBox
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <GlassMorphismCard mb={6}>
        {/* Header with search and filters */}
        <Flex
          p={6}
          direction={{ base: "column", md: "row" }}
          justify="space-between"
          align={{ base: "start", md: "center" }}
          wrap="wrap"
          gap={4}
        >
          {/* Search input */}
          <HStack spacing={4} flex={{ base: "1", md: "2" }} flexWrap="wrap">
            <VStack align="start" spacing={1} minW="250px">
              <Text fontSize="xs" fontWeight="medium" color="gray.500">
                Search Tenants
              </Text>
              <InputGroup size="sm">
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.300" />
                </InputLeftElement>
                <Input
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder="Search by name, namespace, email or phone"
                  borderRadius="md"
                />
              </InputGroup>
              <Text fontSize="xs" color="gray.500" mt={1} ml={1}>
                <Icon as={InfoIcon} boxSize={3} mr={1} />
                Type any keyword to search across all fields
              </Text>
            </VStack>
            <VStack align="start" spacing={1} minW="200px">
              <Text fontSize="xs" fontWeight="medium" color="gray.500">
                Verification Status
              </Text>
              <Select
                size="sm"
                borderRadius="md"
                value={verificationStatus}
                onChange={(e) => setVerificationStatus(e.target.value)}
              >
                <option value="all">All</option>
                <option value="not_initiated">Not Initiated</option>
                <option value="pending">Pending</option>
                <option value="verified">Success</option>
                <option value="rejected">Rejected</option>
              </Select>
            </VStack>
            <Button
              size="sm"
              colorScheme="orange"
              onClick={getTenants}
              borderRadius="md"
              mt={6}
            >
              Search
            </Button>
          </HStack>

          {/* Title */}
          <HStack spacing={2} justify="flex-end">
            <Text fontSize="xl" fontWeight="bold">
              Tenants
            </Text>
            <Badge
              colorScheme="orange"
              fontSize="sm"
              py={1}
              px={2}
              borderRadius="md"
            >
              {filteredTenants.length}
            </Badge>
          </HStack>
        </Flex>

        {/* Table */}
        <TableContainer>
          <Table variant="simple" color="gray.500">
            <Thead bg={theadBgColor}>
              <Tr>
                {tenantTableHeaders.map((item, index) => {
                  return (
                    <Th
                      key={index}
                      py={4}
                      px={6}
                      borderColor={borderColor}
                      textTransform="capitalize"
                      fontSize="xs"
                      fontWeight="bold"
                      letterSpacing="wider"
                      textAlign="center"
                    >
                      <Flex
                        justify="center"
                        align="center"
                        fontSize={{ sm: "10px", lg: "12px" }}
                        color="gray.400"
                      >
                        {item.Header}
                      </Flex>
                    </Th>
                  );
                })}
              </Tr>
            </Thead>
            <Tbody>
              {paginatedData.map((tenant, index) => {
                return (
                  <MotionTr
                    key={tenant.Id}
                    whileHover={{ backgroundColor: hoverBg }}
                    transition={{ duration: 0.2 }}
                  >
                    <Td
                      textAlign="center"
                      py={4}
                      px={6}
                      borderColor={borderColor}
                    >
                      <Text color={textColor} fontSize="sm" fontWeight="600">
                        {currentPage * pageSize + index + 1}
                      </Text>
                    </Td>
                    <Td
                      textAlign="center"
                      py={4}
                      px={6}
                      borderColor={borderColor}
                    >
                      <Text color={textColor} fontSize="sm" fontWeight="600">
                        {tenant?.FirstName + " " + tenant?.LastName}
                      </Text>
                    </Td>
                    <Td
                      textAlign="center"
                      py={4}
                      px={6}
                      borderColor={borderColor}
                    >
                      <Text color={textColor} fontSize="sm" fontWeight="600">
                        {tenant?.NameSpace}
                      </Text>
                    </Td>
                    <Td
                      textAlign="center"
                      py={4}
                      px={6}
                      borderColor={borderColor}
                    >
                      <Text color={textColor} fontSize="sm" fontWeight="600">
                        {tenant?.PhoneNumber}
                      </Text>
                    </Td>
                    <Td
                      textAlign="center"
                      py={4}
                      px={6}
                      borderColor={borderColor}
                    >
                      <Text color={textColor} fontSize="sm" fontWeight="600">
                        {tenant?.PrimaryEmail}
                      </Text>
                    </Td>
                    <Td
                      textAlign="center"
                      py={4}
                      px={6}
                      borderColor={borderColor}
                    >
                      <Flex align="center" justify="center">
                        <TypeBadge type={tenant?.Type} />
                      </Flex>
                    </Td>
                    <Td
                      textAlign="center"
                      py={4}
                      px={6}
                      borderColor={borderColor}
                    >
                      <Flex align="center" justify="center">
                        <GSTBadge isGstPaid={tenant?.IsGstPaid} />
                      </Flex>
                    </Td>
                    <Td
                      textAlign="center"
                      py={4}
                      px={6}
                      borderColor={borderColor}
                    >
                      <Flex align="center" justify="center">
                        <PayoutsEnabledBadge
                          isPayoutsEnabled={tenant?.PayoutsEnabled}
                        />
                      </Flex>
                    </Td>
                    {/* <Td
                      textAlign="center"
                      py={4}
                      px={6}
                      borderColor={borderColor}
                    >
                      <Flex align="center" justify="center">
                        <StatusBadge status={tenant?.VerificationStatus} />
                      </Flex>
                    </Td>
                    <Td
                      textAlign="center"
                      py={4}
                      px={6}
                      borderColor={borderColor}
                    >
                      <Tooltip label="Download Document" placement="top">
                        <IconButton
                          icon={<DownloadIcon />}
                          colorScheme="blue"
                          variant="solid"
                          size="sm"
                          onClick={() =>
                            handleDownload(tenant?.UnderTakingDocumentUrl)
                          }
                          isDisabled={!tenant?.UnderTakingDocumentUrl}
                          borderRadius="full"
                          aria-label="Download Document"
                        />
                      </Tooltip>
                    </Td> */}
                    <Td
                      textAlign="center"
                      py={4}
                      px={6}
                      borderColor={borderColor}
                    >
                      <Flex align="center" justify="center">
                        <Tooltip label="Edit Tenant" placement="top">
                          <IconButton
                            icon={<EditIcon />}
                            colorScheme="blue"
                            variant="ghost"
                            size="sm"
                            onClick={() => viewHandler(tenant)}
                            isDisabled={tenant?.Role === "urzza_cms_admin"}
                            borderRadius="full"
                            aria-label="Edit Tenant"
                          />
                        </Tooltip>
                      </Flex>
                    </Td>
                  </MotionTr>
                );
              })}
            </Tbody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Flex
          p={4}
          justify="space-between"
          align="center"
          borderTop="1px solid"
          borderColor={borderColor}
          flexWrap="wrap"
          gap={4}
        >
          <HStack>
            <Select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              size="sm"
              borderRadius="md"
              w="120px"
            >
              {[5, 8, 10, 20, 30].map((size) => (
                <option key={size} value={size}>
                  Show {size}
                </option>
              ))}
            </Select>
            <Text fontSize="sm" color="gray.500">
              Showing {Math.min(paginatedData.length, pageSize)} of{" "}
              {filteredTenants.length} results
            </Text>
          </HStack>

          <HStack spacing={2}>
            <PaginationButton
              isDisabled={!canPreviousPage}
              onClick={() => gotoPage(0)}
            >
              <ChevronLeftIcon boxSize={4} mr={1} />
              First
            </PaginationButton>

            <PaginationButton
              isDisabled={!canPreviousPage}
              onClick={() => previousPage()}
            >
              <ChevronLeftIcon boxSize={4} />
            </PaginationButton>

            {pageCount <= 5 ? (
              // If we have 5 or fewer pages, show all page buttons
              [...Array(pageCount)].map((_, i) => (
                <PaginationButton
                  key={i}
                  isActive={currentPage === i}
                  onClick={() => gotoPage(i)}
                >
                  {i + 1}
                </PaginationButton>
              ))
            ) : (
              // If we have more than 5 pages, show a subset
              <>
                {currentPage > 1 && (
                  <PaginationButton onClick={() => gotoPage(0)}>
                    1
                  </PaginationButton>
                )}

                {currentPage > 2 && <Text>...</Text>}

                {currentPage > 0 && (
                  <PaginationButton onClick={() => gotoPage(currentPage - 1)}>
                    {currentPage}
                  </PaginationButton>
                )}

                <PaginationButton
                  isActive
                  onClick={() => gotoPage(currentPage)}
                >
                  {currentPage + 1}
                </PaginationButton>

                {currentPage < pageCount - 1 && (
                  <PaginationButton onClick={() => gotoPage(currentPage + 1)}>
                    {currentPage + 2}
                  </PaginationButton>
                )}

                {currentPage < pageCount - 3 && <Text>...</Text>}

                {currentPage < pageCount - 2 && (
                  <PaginationButton onClick={() => gotoPage(pageCount - 1)}>
                    {pageCount}
                  </PaginationButton>
                )}
              </>
            )}

            <PaginationButton
              isDisabled={!canNextPage}
              onClick={() => nextPage()}
            >
              <ChevronRightIcon boxSize={4} />
            </PaginationButton>

            <PaginationButton
              isDisabled={!canNextPage}
              onClick={() => gotoPage(pageCount - 1)}
            >
              Last
              <ChevronRightIcon boxSize={4} ml={1} />
            </PaginationButton>
          </HStack>
        </Flex>

        {/* Empty state */}
        {filteredTenants.length === 0 && <EmptyStateIllustration />}
      </GlassMorphismCard>

      {/* Drawers */}
      {createDrawerState?.isOpen && <CreateTenants update={updateHandler} />}
      {viewTenantsState?.isOpen && (
        <ViewTenants
          getTenants={getTenants}
          tenantsData={viewTenantsState?.data}
          onClose={() =>
            setViewTenantsState({
              isOpen: false,
              data: null,
            })
          }
        />
      )}
    </MotionBox>
  );
};

export default TenantsList;
